// Goals API — Standing Goals Agent (Phase A)
// All routes go through /api/v1/autonomy on the gateway → agent service.

import { authenticatedFetch } from './authenticatedFetch';

// The autonomy router is mounted at /api/v1/autonomy ON THE AGENT SERVICE.
// The gateway forwards /api/v1/agent/* → agent service /api/v1/* (it strips
// /api/v1/agent and re-adds /api/v1), so the frontend must address it as
// /api/v1/agent/autonomy/* — same shape as agentMemory.ts (/api/v1/agent/memory).
const AUTONOMY = '/api/v1/agent/autonomy';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ProgressNote {
  date: string;
  note: string;
}

export type AuthorizationMode = 'read_only' | 'approve_each' | 'auto_within_bounds';

export interface ResourceScope {
  domains: string[];
  project_ids: string[];
}

export interface AuthorizationPolicy {
  mode: AuthorizationMode;
  allowed_tools: string[];
  resource_scope: ResourceScope;
  credit_ceiling_per_run: number | null;
  credit_ceiling_per_month: number | null;
}

export interface Goal {
  id: string;
  title: string;
  description: string | null;
  target_metric: string | null;
  target_value: string | null;
  baseline_value: string | null;
  current_value: string | null;
  current_value_num: number | null;
  deadline: string | null;
  status: 'active' | 'completed' | 'paused' | 'abandoned';
  template_key: string | null;
  scheduled_task_id: string | null;
  last_run_at: string | null;
  // Timing of the bound schedule (joined server-side). Null when the goal has
  // no bound schedule (e.g. a manually created goal with no template).
  next_run_at: string | null;
  schedule_kind: string | null;
  interval_minutes: number | null;
  cron_expr: string | null;
  schedule_enabled: boolean | null;
  progress_notes: ProgressNote[];
  authorization_policy: AuthorizationPolicy;
  /** Only present on GET /goals/{id} (not the list endpoint). */
  credits_used_this_month?: number;
  created_at: string;
  updated_at: string;
}

export interface GoalTemplate {
  key: string;
  name: string;
  description: string;
  icon: string;
  schedule_kind: string;
  interval_minutes: number | null;
  cron_expr: string | null;
  allow_writes: boolean;
  default_target_metric: string;
  available_phase: string;
}

export interface GoalRun {
  id: string;
  task_id: string | null;
  goal_id: string | null;
  status: 'running' | 'completed' | 'failed' | 'needs_approval';
  approval_status: 'pending' | 'approved' | 'rejected' | 'auto_applied' | null;
  credits_spent: number;
  prompt: string;
  result: string | null;
  error: string | null;
  session_id: string | null;
  iterations: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

// ── Goal queries ──────────────────────────────────────────────────────────────
// authenticatedFetch<T> returns the parsed body of type T and throws an Error
// (with .status / .detail) on a non-2xx response — so callers just await it.

export async function listGoals(status?: string): Promise<Goal[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const data = await authenticatedFetch<{ goals: Goal[] }>(`${AUTONOMY}/goals${qs}`, {
    serviceName: 'Goals',
  });
  return data.goals;
}

export async function getGoal(goalId: string): Promise<Goal> {
  const data = await authenticatedFetch<{ goal: Goal }>(
    `${AUTONOMY}/goals/${encodeURIComponent(goalId)}`,
    { serviceName: 'Goals' },
  );
  return data.goal;
}

export async function listGoalRuns(goalId: string, limit = 20): Promise<GoalRun[]> {
  const data = await authenticatedFetch<{ runs: GoalRun[] }>(
    `${AUTONOMY}/goals/${encodeURIComponent(goalId)}/runs?limit=${limit}`,
    { serviceName: 'Goals' },
  );
  return data.runs;
}

export async function listTemplates(): Promise<GoalTemplate[]> {
  const data = await authenticatedFetch<{ templates: GoalTemplate[] }>(`${AUTONOMY}/templates`, {
    serviceName: 'Goals',
  });
  return data.templates;
}

// ── Goal mutations ────────────────────────────────────────────────────────────

export async function createGoalFromTemplate(
  templateKey: string,
  titleOverride?: string,
): Promise<{ goal: Goal; task: unknown }> {
  return authenticatedFetch(`${AUTONOMY}/goals/from-template`, {
    method: 'POST',
    body: JSON.stringify({ template_key: templateKey, title_override: titleOverride ?? null }),
    serviceName: 'Goals',
  });
}

export async function createGoal(params: {
  title: string;
  description?: string;
  target_metric?: string;
  target_value?: string;
  baseline_value?: string;
  deadline?: string;
}): Promise<{ success: boolean; goal: Goal }> {
  return authenticatedFetch(`${AUTONOMY}/goals`, {
    method: 'POST',
    body: JSON.stringify(params),
    serviceName: 'Goals',
  });
}

export async function updateGoal(goalId: string, params: {
  status?: string;
  current_value?: string;
  target_value?: string;
  deadline?: string;
  progress_note?: string;
}): Promise<{ success: boolean; goal: Goal }> {
  return authenticatedFetch(`${AUTONOMY}/goals/${encodeURIComponent(goalId)}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    serviceName: 'Goals',
  });
}

export async function archiveGoal(goalId: string): Promise<{ success: boolean }> {
  return authenticatedFetch(`${AUTONOMY}/goals/${encodeURIComponent(goalId)}`, {
    method: 'DELETE',
    serviceName: 'Goals',
  });
}

export async function setGoalAuthorization(
  goalId: string,
  policy: {
    mode: AuthorizationMode;
    allowed_tools?: string[];
    resource_scope?: Partial<ResourceScope>;
    credit_ceiling_per_run?: number | null;
    credit_ceiling_per_month?: number | null;
  },
): Promise<{ success: boolean; goal: Goal }> {
  return authenticatedFetch(`${AUTONOMY}/goals/${encodeURIComponent(goalId)}/authorization`, {
    method: 'PUT',
    body: JSON.stringify({
      allowed_tools: [],
      resource_scope: {},
      ...policy,
    }),
    serviceName: 'Goals',
  });
}

export async function listWriteTools(): Promise<string[]> {
  const data = await authenticatedFetch<{ tools: string[] }>(`${AUTONOMY}/write-tools`, {
    serviceName: 'Goals',
  });
  return data.tools;
}

// ── Scheduled tasks (agent automations, incl. one-shot job follow-ups) ────────

export interface ScheduledTask {
  id: string;
  name: string;
  task_prompt: string;
  schedule_kind: 'cron' | 'interval' | 'once';
  cron_expr: string | null;
  interval_minutes: number | null;
  next_run_at: string | null;
  last_run_at: string | null;
  last_status: string | null;
  run_count: number;
  enabled: boolean;
  notify: boolean;
  allow_writes: boolean;
  created_at: string;
}

export async function listSchedules(): Promise<ScheduledTask[]> {
  const data = await authenticatedFetch<{ scheduled_tasks: ScheduledTask[] }>(
    `${AUTONOMY}/schedules`,
    { serviceName: 'Goals' },
  );
  return data.scheduled_tasks;
}

export async function deleteSchedule(taskId: string): Promise<{ success: boolean }> {
  return authenticatedFetch(`${AUTONOMY}/schedules/${encodeURIComponent(taskId)}`, {
    method: 'DELETE',
    serviceName: 'Goals',
  });
}

/** Human cadence for a scheduled task. */
export function formatTaskCadence(task: ScheduledTask): string {
  if (task.schedule_kind === 'once') return 'One-time';
  if (task.interval_minutes === 1440) return 'Daily';
  if (task.interval_minutes === 10080) return 'Weekly';
  if (task.interval_minutes === 20160) return 'Bi-weekly';
  if (task.cron_expr) return task.cron_expr;
  if (task.interval_minutes) return `Every ${task.interval_minutes} min`;
  return 'Scheduled';
}

/** Compact run-time label (e.g. "Jul 6, 2:15 PM"), or null. */
export function formatTaskTime(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

// ── Display helpers ───────────────────────────────────────────────────────────

export function formatInterval(tmpl: GoalTemplate): string {
  if (tmpl.interval_minutes === 1440) return 'Daily';
  if (tmpl.interval_minutes === 10080) return 'Weekly';
  if (tmpl.interval_minutes === 20160) return 'Bi-weekly';
  if (tmpl.cron_expr) return tmpl.cron_expr;
  return `Every ${tmpl.interval_minutes ?? '?'} min`;
}

/** Human cadence for a goal's bound schedule, or null when it has none. */
export function formatGoalCadence(goal: Goal): string | null {
  if (goal.schedule_kind === 'once') return 'One-time';
  if (goal.interval_minutes === 1440) return 'Daily';
  if (goal.interval_minutes === 10080) return 'Weekly';
  if (goal.interval_minutes === 20160) return 'Bi-weekly';
  if (goal.cron_expr) return goal.cron_expr;
  if (goal.interval_minutes) return `Every ${goal.interval_minutes} min`;
  return null;
}

/** Compact "next run" label (e.g. "Jul 4, 7:00 AM"), or null when unscheduled. */
export function formatNextRun(goal: Goal): string | null {
  if (!goal.next_run_at || goal.schedule_enabled === false) return null;
  return new Date(goal.next_run_at).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}
